import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PropertyProvider } from "@/context/PropertyContext";
import useBrandingTitleAndFavicon from "@/hooks/useBrandingTitleAndFavicon";
import Layout from "@/components/app/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Properties from "@/pages/Properties";
import Zones from "@/pages/Zones";
import Checklists from "@/pages/Checklists";
import ChecklistEditor from "@/pages/ChecklistEditor";
import Tasks from "@/pages/Tasks";
import MyTasks from "@/pages/MyTasks";
import Tickets from "@/pages/Tickets";
import Staff from "@/pages/Staff";
import Reports from "@/pages/Reports";
import Attendance from "@/pages/Attendance";
import MyShifts from "@/pages/MyShifts";
import Shifts from "@/pages/Shifts";
import Turnover from "@/pages/Turnover";
import Bookings from "@/pages/Bookings";
import ConductPage from "@/pages/ConductPage";
import Roles from "@/pages/Roles";
import Payroll from "@/pages/Payroll";
import SalaryStructures from "@/pages/SalaryStructures";
import HrDashboard from "@/pages/HrDashboard";
import ManagementDashboard from "@/pages/ManagementDashboard";
import MyPayslips from "@/pages/MyPayslips";
import Advances from "@/pages/Advances";
import MyAdvances from "@/pages/MyAdvances";
import LeaveBalances from "@/pages/LeaveBalances";
import MyLeaveBalance from "@/pages/MyLeaveBalance";
import WarningsAdmin from "@/pages/WarningsAdmin";
import GroomingChecks from "@/pages/GroomingChecks";
import MyWarnings from "@/pages/MyWarnings";
import Apology from "@/pages/Apology";
import ApologiesAdmin from "@/pages/ApologiesAdmin";
import MoodDashboard from "@/pages/MoodDashboard";
import PendingApprovals from "@/pages/PendingApprovals";
import TeamTenure from "@/pages/TeamTenure";
import MyProfile from "@/pages/MyProfile";
import Branding from "@/pages/Branding";
import EmailSettings from "@/pages/EmailSettings";
import EmailLogs from "@/pages/EmailLogs";
import EmailTemplates from "@/pages/EmailTemplates";
import IdCards from "@/pages/IdCards";
import Kiosk from "@/pages/Kiosk";
import UniformCategories from "@/pages/UniformCategories";
import UniformFulfilment from "@/pages/UniformFulfilment";
import LeavePolicies from "@/pages/LeavePolicies";
import ErrorBoundary from "@/components/app/ErrorBoundary";
import { Loader2 } from "lucide-react";

function ProtectedShell({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  // Deactivated users are routed to /apology — they can ONLY see that screen.
  if (user && user.active === false) return <Navigate to="/apology" replace />;
  return <Layout><ErrorBoundary>{children}</ErrorBoundary></Layout>;
}

function ApologyGate({ children }) {
  // /apology is accessible to deactivated users WITHOUT the Layout chrome.
  // Active users with no hard warnings are redirected back to dashboard.
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.active !== false && (user.warnings_summary?.hard_count ?? 0) === 0) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function PermissionGate({ perm, roles, children }) {
  const { user, hasPermission, loading } = useAuth();
  if (loading) return null;
  if (roles && roles.length > 0) {
    if (!user || !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  } else if (perm && !hasPermission(perm)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function BrandingApplier() {
  useBrandingTitleAndFavicon();
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PropertyProvider>
            <BrandingApplier />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/kiosk" element={<Kiosk />} />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<ProtectedShell><Dashboard /></ProtectedShell>} />
              <Route path="/properties" element={<ProtectedShell><PermissionGate perm="properties"><Properties /></PermissionGate></ProtectedShell>} />
              <Route path="/zones" element={<ProtectedShell><PermissionGate perm="zones"><Zones /></PermissionGate></ProtectedShell>} />
              <Route path="/checklists" element={<ProtectedShell><PermissionGate perm="checklists"><Checklists /></PermissionGate></ProtectedShell>} />
              <Route path="/checklists/new" element={<ProtectedShell><PermissionGate perm="checklists"><ChecklistEditor /></PermissionGate></ProtectedShell>} />
              <Route path="/checklists/:id/edit" element={<ProtectedShell><PermissionGate perm="checklists"><ChecklistEditor /></PermissionGate></ProtectedShell>} />
              <Route path="/tasks" element={<ProtectedShell><PermissionGate perm="tasks"><Tasks /></PermissionGate></ProtectedShell>} />
              <Route path="/my-tasks" element={<ProtectedShell><PermissionGate perm="my_tasks"><MyTasks /></PermissionGate></ProtectedShell>} />
              <Route path="/tickets" element={<ProtectedShell><PermissionGate perm="tickets"><Tickets /></PermissionGate></ProtectedShell>} />
              <Route path="/staff" element={<ProtectedShell><PermissionGate perm="staff"><Staff /></PermissionGate></ProtectedShell>} />
              <Route path="/reports" element={<ProtectedShell><PermissionGate perm="reports"><Reports /></PermissionGate></ProtectedShell>} />
              <Route path="/attendance" element={<ProtectedShell><PermissionGate perm="attendance"><Attendance /></PermissionGate></ProtectedShell>} />
              <Route path="/my-shifts" element={<ProtectedShell><PermissionGate perm="shifts"><MyShifts /></PermissionGate></ProtectedShell>} />
              <Route path="/shifts" element={<ProtectedShell><PermissionGate perm="shifts_manage"><Shifts /></PermissionGate></ProtectedShell>} />
              <Route path="/turnover" element={<ProtectedShell><PermissionGate perm="turnover"><Turnover /></PermissionGate></ProtectedShell>} />
              <Route path="/bookings" element={<ProtectedShell><PermissionGate perm="bookings"><Bookings /></PermissionGate></ProtectedShell>} />
              <Route path="/conduct" element={<ProtectedShell><ConductPage /></ProtectedShell>} />
              <Route path="/leave-policies" element={<ProtectedShell><LeavePolicies /></ProtectedShell>} />
              <Route path="/roles" element={<ProtectedShell><PermissionGate perm="roles"><Roles /></PermissionGate></ProtectedShell>} />
              <Route path="/payroll" element={<ProtectedShell><PermissionGate perm="payroll"><Payroll /></PermissionGate></ProtectedShell>} />
              <Route path="/salary-structures" element={<ProtectedShell><PermissionGate perm="salary_structures"><SalaryStructures /></PermissionGate></ProtectedShell>} />
              <Route path="/hr-dashboard" element={<ProtectedShell><PermissionGate perm="hr_dashboard"><HrDashboard /></PermissionGate></ProtectedShell>} />
              <Route path="/mgmt-dashboard" element={<ProtectedShell><PermissionGate perm="mgmt_dashboard"><ManagementDashboard /></PermissionGate></ProtectedShell>} />
              <Route path="/my-payslips" element={<ProtectedShell><PermissionGate perm="my_payslips"><MyPayslips /></PermissionGate></ProtectedShell>} />
              <Route path="/advances" element={<ProtectedShell><PermissionGate perm="advances"><Advances /></PermissionGate></ProtectedShell>} />
              <Route path="/my-advances" element={<ProtectedShell><PermissionGate perm="my_advances"><MyAdvances /></PermissionGate></ProtectedShell>} />
              <Route path="/leave-balances" element={<ProtectedShell><PermissionGate perm="leave_balances"><LeaveBalances /></PermissionGate></ProtectedShell>} />
              <Route path="/my-leave-balance" element={<ProtectedShell><PermissionGate perm="my_leave_balance"><MyLeaveBalance /></PermissionGate></ProtectedShell>} />
              <Route path="/warnings" element={<ProtectedShell><PermissionGate perm="warnings"><WarningsAdmin /></PermissionGate></ProtectedShell>} />
              <Route path="/grooming-checks" element={<ProtectedShell><PermissionGate perm="grooming_review"><GroomingChecks /></PermissionGate></ProtectedShell>} />
              <Route path="/my-warnings" element={<ProtectedShell><PermissionGate perm="my_warnings"><MyWarnings /></PermissionGate></ProtectedShell>} />
              <Route path="/apologies" element={<ProtectedShell><PermissionGate perm="apologies"><ApologiesAdmin /></PermissionGate></ProtectedShell>} />
              <Route path="/mood" element={<ProtectedShell><PermissionGate perm="mood_dashboard"><MoodDashboard /></PermissionGate></ProtectedShell>} />
              <Route path="/pending-approvals" element={<ProtectedShell><PermissionGate perm="pending_approvals"><PendingApprovals /></PermissionGate></ProtectedShell>} />
              <Route path="/team-tenure" element={<ProtectedShell><PermissionGate perm="team_tenure"><TeamTenure /></PermissionGate></ProtectedShell>} />
              <Route path="/profile" element={<ProtectedShell><MyProfile /></ProtectedShell>} />
              <Route path="/branding" element={<ProtectedShell><PermissionGate perm="branding"><Branding /></PermissionGate></ProtectedShell>} />
              <Route path="/email/settings" element={<ProtectedShell><PermissionGate perm="email_settings"><EmailSettings /></PermissionGate></ProtectedShell>} />
              <Route path="/email/logs" element={<ProtectedShell><PermissionGate perm="email_logs"><EmailLogs /></PermissionGate></ProtectedShell>} />
              <Route path="/email/templates" element={<ProtectedShell><PermissionGate perm="email_templates"><EmailTemplates /></PermissionGate></ProtectedShell>} />
              <Route path="/id-cards" element={<ProtectedShell><PermissionGate perm="id_cards"><IdCards /></PermissionGate></ProtectedShell>} />
              <Route path="/uniform-categories" element={<ProtectedShell><PermissionGate perm="uniforms_manage"><UniformCategories /></PermissionGate></ProtectedShell>} />
              <Route path="/uniform-fulfilment" element={<ProtectedShell><PermissionGate perm="uniforms_manage"><UniformFulfilment /></PermissionGate></ProtectedShell>} />
              <Route path="/apology" element={<ApologyGate><Apology /></ApologyGate>} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </PropertyProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
